import type { Database } from '@/integrations/supabase/types';

export type DenialCategory = Database['public']['Enums']['denial_category'];
export type DenialCategoryConfidence = Database['public']['Enums']['denial_category_confidence'];

// Rules-based classifier for denial reasons
const DENIAL_PATTERNS: Array<{ pattern: RegExp; category: DenialCategory }> = [
  // MEDICAL_NECESSITY - check first as it's most specific
  { 
    pattern: /medical\s*necessity|not\s*medically\s*necessary|lack\s*of\s*medical\s*necessity|clinical\s*necessity/i, 
    category: 'MEDICAL_NECESSITY' 
  },
  
  // DOCUMENTATION_MISSING
  { 
    pattern: /missing\s*doc|insufficient\s*doc|incomplete\s*doc|documentation\s*not|lack.*documentation|additional\s*records\s*needed|clinical\s*notes\s*missing/i, 
    category: 'DOCUMENTATION_MISSING' 
  },
  
  // CODING_MODIFIER
  { 
    pattern: /modifier|coding|cpt\s*code|incorrect\s*code|bundl|unbundl|e\/m\s*code|hcpcs|code\s*mismatch|procedure\s*code/i, 
    category: 'CODING_MODIFIER' 
  },
  
  // TIMELY_FILING - check before coverage patterns
  { 
    pattern: /timely\s*filing|late\s*submission|filing\s*deadline|submission\s*deadline|past\s*deadline|exceeded.*time/i, 
    category: 'TIMELY_FILING' 
  },
  
  // NON_COVERED_SERVICE
  { 
    pattern: /not\s*covered|non.?covered|excluded\s*service|experimental|investigational|cosmetic|not\s*a\s*benefit/i, 
    category: 'NON_COVERED_SERVICE' 
  },
  
  // COVERAGE_LIMITATION
  { 
    pattern: /coverage|benefit\s*limit|max.*reached|exceeded.*limit|frequency\s*limit|plan\s*exclusion|out\s*of\s*network|authorization\s*expired/i, 
    category: 'COVERAGE_LIMITATION' 
  },
];

export interface DenialClassification {
  category: DenialCategory;
  confidence: DenialCategoryConfidence;
  matchedPattern?: string;
}

export function classifyDenialReason(rawText: string | null | undefined): DenialClassification {
  if (!rawText) {
    return { category: 'OTHER', confidence: 'TEMPLATE' };
  }
  
  const trimmed = rawText.trim().toLowerCase();
  
  for (const { pattern, category } of DENIAL_PATTERNS) {
    if (pattern.test(trimmed)) {
      const match = trimmed.match(pattern);
      return { 
        category, 
        confidence: 'TEMPLATE',
        matchedPattern: match?.[0]
      };
    }
  }
  
  return { category: 'OTHER', confidence: 'TEMPLATE' };
}

export const DENIAL_CATEGORY_LABELS: Record<DenialCategory, string> = {
  MEDICAL_NECESSITY: 'Medical Necessity',
  DOCUMENTATION_MISSING: 'Documentation Missing',
  CODING_MODIFIER: 'Coding/Modifier Issue',
  COVERAGE_LIMITATION: 'Coverage Limitation',
  TIMELY_FILING: 'Timely Filing',
  NON_COVERED_SERVICE: 'Non-Covered Service',
  OTHER: 'Other',
};

export const DENIAL_CATEGORY_DESCRIPTIONS: Record<DenialCategory, string> = {
  MEDICAL_NECESSITY: 'The payer determined the procedure was not medically necessary based on submitted documentation.',
  DOCUMENTATION_MISSING: 'Required clinical documentation was not provided or was insufficient.',
  CODING_MODIFIER: 'Issues with CPT codes, modifiers, or code bundling rules.',
  COVERAGE_LIMITATION: 'The procedure exceeded plan benefits, frequency limits, or network restrictions.',
  TIMELY_FILING: 'The claim or authorization was submitted past the deadline.',
  NON_COVERED_SERVICE: 'The service is explicitly excluded from coverage.',
  OTHER: 'The denial reason does not fit standard categories.',
};

export const DENIAL_CATEGORY_COLORS: Record<DenialCategory, string> = {
  MEDICAL_NECESSITY: 'bg-red-500/10 text-red-500 border-red-500/30',
  DOCUMENTATION_MISSING: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  CODING_MODIFIER: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  COVERAGE_LIMITATION: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  TIMELY_FILING: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  NON_COVERED_SERVICE: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  OTHER: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
};

export const CONFIDENCE_LABELS: Record<DenialCategoryConfidence, string> = {
  TEMPLATE: 'Auto-classified',
  VERIFIED: 'Human Verified',
  LEARNED: 'ML Learned',
};
